import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Select, message } from "antd";
import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { createSegment, previewSegment, updateSegment } from "./service";
import styles from "./SegmentBuilder.module.scss";

const FIELD_OPTIONS = [
  {
    value: "transactional.product_group",
    label: "Transactional → Product group",
    type: "text",
  },
  {
    value: "transactional.pax",
    label: "Transactional → Party size",
    type: "number",
  },
  {
    value: "transactional.lead_days",
    label: "Transactional → Lead days",
    type: "number",
  },
  {
    value: "behavioral.last_event",
    label: "Behavioral → Last event",
    type: "date",
  },
  { value: "app.event_name", label: "App → Event name", type: "event" },
  { value: "web.event_name", label: "Web → Event name", type: "event" },
  { value: "voc.nps", label: "VoC → NPS", type: "nps" },
  { value: "voc.sentiment", label: "VoC → Sentiment", type: "sentiment" },
  { value: "voc.has_feedback", label: "VoC → Has feedback", type: "flag" },
];

const OPERATORS_BY_TYPE = {
  text: [{ value: "equals", label: "equals" }],
  number: [
    { value: "gte", label: "is at least" },
    { value: "lt", label: "is less than" },
  ],
  date: [
    { value: "within_last_days", label: "within the last (days)" },
    { value: "older_than_days", label: "older than (days)" },
  ],
  event: [
    { value: "at_least_once", label: "at least once" },
    { value: "at_least_twice", label: "at least twice" },
    { value: "never", label: "never" },
  ],
  nps: [
    { value: "lte", label: "is at most" },
    { value: "gte", label: "is at least" },
  ],
  sentiment: [{ value: "lte", label: "is at most" }],
  flag: [{ value: "is_true", label: "is true" }],
};

const STARTER_TEMPLATES = [
  {
    id: "never_addon",
    name: "Bought category, never addon",
    rule_ast: {
      op: "and",
      rules: [
        { op: "event_count", event: "hotel_booked", gte: 1 },
        { op: "event_count_lt", event: "insurance_added", lt: 1 },
      ],
    },
  },
  {
    id: "repeat",
    name: "Repeat purchasers",
    rule_ast: { op: "event_count", event: "reservation_created", gte: 2 },
  },
  {
    id: "lapsed90",
    name: "Lapsed 90+ days",
    rule_ast: { op: "last_event_older_than_days", days: 90 },
  },
  {
    id: "active30",
    name: "Active last 30 days",
    rule_ast: { op: "last_event_within_days", days: 30 },
  },
  {
    id: "family",
    name: "Party size 3+",
    rule_ast: {
      op: "and",
      rules: [
        { op: "event_count", event: "hotel_booked", gte: 1 },
        { op: "trait_gte", key: "pax", value: 3 },
      ],
    },
  },
  {
    id: "last_minute",
    name: "Last-minute (lead < 7d)",
    rule_ast: {
      op: "and",
      rules: [
        { op: "event_count", event: "hotel_booked", gte: 1 },
        { op: "trait_lt", key: "lead_days", value: 7 },
      ],
    },
  },
  {
    id: "early",
    name: "Early booker (lead ≥ 60d)",
    rule_ast: {
      op: "and",
      rules: [
        { op: "event_count", event: "hotel_booked", gte: 1 },
        { op: "trait_gte", key: "lead_days", value: 60 },
      ],
    },
  },
  {
    id: "tour",
    name: "Outbound / tour buyer",
    rule_ast: { op: "event_count", event: "tour_booked", gte: 1 },
  },
];

export const newCondition = () => ({
  id:
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `c-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  field: "",
  operator: "",
  value: "",
});

const ruleToCondition = (rule) => {
  if (!rule || typeof rule !== "object") return null;
  const op = rule.op;
  if (op === "trait_eq" && rule.value != null && String(rule.value).trim()) {
    return {
      ...newCondition(),
      field: "transactional.product_group",
      operator: "equals",
      value: String(rule.value),
    };
  }
  if (op === "last_event_within_days" && rule.days != null) {
    return {
      ...newCondition(),
      field: "behavioral.last_event",
      operator: "within_last_days",
      value: String(rule.days),
    };
  }
  if (op === "last_event_older_than_days" && rule.days != null) {
    return {
      ...newCondition(),
      field: "behavioral.last_event",
      operator: "older_than_days",
      value: String(rule.days),
    };
  }
  if (op === "event_count" && rule.event) {
    return {
      ...newCondition(),
      field: "app.event_name",
      operator: Number(rule.gte) >= 2 ? "at_least_twice" : "at_least_once",
      value: String(rule.event),
    };
  }
  if (op === "event_count_lt" && rule.event) {
    return {
      ...newCondition(),
      field: "app.event_name",
      operator: "never",
      value: String(rule.event),
    };
  }
  if (op === "trait_gte" && rule.key === "pax" && rule.value != null) {
    return {
      ...newCondition(),
      field: "transactional.pax",
      operator: "gte",
      value: String(rule.value),
    };
  }
  if (op === "trait_gte" && rule.key === "lead_days" && rule.value != null) {
    return {
      ...newCondition(),
      field: "transactional.lead_days",
      operator: "gte",
      value: String(rule.value),
    };
  }
  if (op === "trait_lt" && rule.key === "lead_days" && rule.value != null) {
    return {
      ...newCondition(),
      field: "transactional.lead_days",
      operator: "lt",
      value: String(rule.value),
    };
  }
  if (op === "nps_lte" && rule.value != null) {
    return {
      ...newCondition(),
      field: "voc.nps",
      operator: "lte",
      value: String(rule.value),
    };
  }
  if (op === "nps_gte" && rule.value != null) {
    return {
      ...newCondition(),
      field: "voc.nps",
      operator: "gte",
      value: String(rule.value),
    };
  }
  if (op === "sentiment_lte" && rule.value != null) {
    return {
      ...newCondition(),
      field: "voc.sentiment",
      operator: "lte",
      value: String(rule.value),
    };
  }
  if (op === "has_voc") {
    return {
      ...newCondition(),
      field: "voc.has_feedback",
      operator: "is_true",
      value: "",
    };
  }
  return null;
};

export const ruleAstToDraft = (ruleAst) => {
  if (!ruleAst || typeof ruleAst !== "object") {
    return { joinOperator: "AND", conditions: [newCondition()], mapped: false };
  }
  let joinOperator = "AND";
  let rules = [ruleAst];
  if (ruleAst.op === "and" || ruleAst.op === "or") {
    joinOperator = ruleAst.op === "or" ? "OR" : "AND";
    rules = Array.isArray(ruleAst.rules) ? ruleAst.rules : [];
  }
  const conditions = rules.map(ruleToCondition).filter(Boolean);
  return {
    joinOperator,
    conditions: conditions.length ? conditions : [newCondition()],
    mapped: conditions.length > 0,
  };
};

export const conditionsToRuleAst = (joinOperator, conditions) => {
  const rules = (conditions || [])
    .map((condition) => {
      const field = condition.field;
      const operator = condition.operator;
      const value = String(condition.value || "").trim();
      if (field === "transactional.product_group" && operator === "equals" && value) {
        return { op: "trait_eq", key: "product_group", value };
      }
      if (field === "behavioral.last_event") {
        const days = parseInt(value, 10);
        if (!Number.isFinite(days) || days < 1 || days > 365) return null;
        if (operator === "within_last_days") {
          return { op: "last_event_within_days", days };
        }
        if (operator === "older_than_days") {
          return { op: "last_event_older_than_days", days };
        }
      }
      if ((field === "app.event_name" || field === "web.event_name") && value) {
        if (operator === "never") {
          return { op: "event_count_lt", event: value.toLowerCase(), lt: 1 };
        }
        if (!["at_least_once", "at_least_twice", "equals"].includes(operator)) {
          return null;
        }
        return {
          op: "event_count",
          event: value.toLowerCase(),
          gte: operator === "at_least_twice" ? 2 : 1,
        };
      }
      if (field === "transactional.pax" || field === "transactional.lead_days") {
        const amount = parseInt(value, 10);
        if (!Number.isFinite(amount) || amount < 0) return null;
        const key = field === "transactional.pax" ? "pax" : "lead_days";
        if (operator === "gte") return { op: "trait_gte", key, value: amount };
        if (operator === "lt") return { op: "trait_lt", key, value: amount };
      }
      if (field === "voc.nps") {
        const nps = parseInt(value, 10);
        if (!Number.isFinite(nps) || nps < 0 || nps > 10) return null;
        if (operator === "lte") return { op: "nps_lte", value: nps };
        if (operator === "gte") return { op: "nps_gte", value: nps };
      }
      if (field === "voc.sentiment" && operator === "lte") {
        const score = parseFloat(value);
        if (!Number.isFinite(score) || score < -1 || score > 1) return null;
        return { op: "sentiment_lte", value: score };
      }
      if (field === "voc.has_feedback") return { op: "has_voc" };
      return null;
    })
    .filter(Boolean);
  if (!rules.length) return null;
  if (rules.length === 1) return rules[0];
  return { op: joinOperator === "OR" ? "or" : "and", rules };
};

const fieldMeta = (field) => FIELD_OPTIONS.find((item) => item.value === field);

const ConditionRow = ({ condition, onChange, onRemove, t }) => {
  const field = fieldMeta(condition.field);
  const operators = field ? OPERATORS_BY_TYPE[field.type] || [] : [];
  const needsValue = field && field.type !== "flag";
  return (
    <div className={styles.row}>
      <Select
        className={styles.field}
        placeholder={t("abs.builder_field", "Select field…")}
        value={condition.field || undefined}
        onChange={(value) =>
          onChange({ ...condition, field: value, operator: "", value: "" })
        }
        options={FIELD_OPTIONS.map((item) => ({
          value: item.value,
          label: item.label,
        }))}
      />
      <Select
        className={styles.operator}
        placeholder={t("abs.builder_operator", "Operator…")}
        value={condition.operator || undefined}
        onChange={(value) => onChange({ ...condition, operator: value })}
        disabled={!field}
        options={operators.map((item) => ({
          value: item.value,
          label: item.label,
        }))}
      />
      {needsValue ? (
        <Input
          className={styles.value}
          placeholder={t("abs.builder_value", "Value")}
          value={condition.value}
          onChange={(event) =>
            onChange({ ...condition, value: event.target.value })
          }
          disabled={!field}
        />
      ) : (
        <span className={styles.valueSpacer} />
      )}
      <button
        type="button"
        className={styles.remove}
        onClick={onRemove}
        aria-label={t("abs.builder_remove", "Remove condition")}
      >
        <X size={16} strokeWidth={2} />
      </button>
    </div>
  );
};

const SegmentBuilder = ({
  draft,
  isTest = false,
  onSaved,
  onViewLibrary,
  applyForbidden,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [joinOperator, setJoinOperator] = useState("AND");
  const [conditions, setConditions] = useState([newCondition()]);
  const [editId, setEditId] = useState(null);
  const [refineFromAi, setRefineFromAi] = useState(false);
  const [mappedConditions, setMappedConditions] = useState(true);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templateId, setTemplateId] = useState(undefined);

  const ruleAst = useMemo(
    () => conditionsToRuleAst(joinOperator, conditions),
    [joinOperator, conditions]
  );

  useEffect(() => {
    if (!draft) return;
    setName(draft.name || "");
    setJoinOperator(draft.joinOperator || "AND");
    setConditions(
      draft.conditions && draft.conditions.length
        ? draft.conditions
        : [newCondition()]
    );
    setEditId(draft.editId || null);
    setRefineFromAi(Boolean(draft.refineFromAi));
    setMappedConditions(draft.mappedConditions !== false);
    setPreviewCount(null);
    setTemplateId(undefined);
  }, [draft?.key]);

  useEffect(() => {
    if (!ruleAst) {
      setPreviewCount(null);
      setPreviewLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      const res = await previewSegment({
        rule_ast: ruleAst,
        join_operator: joinOperator,
        conditions,
        is_test: Boolean(isTest),
      });
      if (cancelled) return;
      setPreviewLoading(false);
      if (res?.error_code === "ABS_ENTITLEMENT_REQUIRED") {
        if (applyForbidden) applyForbidden(res);
        return;
      }
      if (!res || res.error || res.database_ready === false) {
        setPreviewCount(null);
        return;
      }
      setPreviewCount(typeof res.count === "number" ? res.count : null);
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // applyForbidden is stable enough for a 403 lock; omit to avoid preview loops.
  }, [ruleAst, joinOperator, conditions, isTest]);

  const resetForm = () => {
    setName("");
    setJoinOperator("AND");
    setConditions([newCondition()]);
    setEditId(null);
    setRefineFromAi(false);
    setMappedConditions(true);
    setPreviewCount(null);
    setTemplateId(undefined);
  };

  const applyTemplate = (id) => {
    setTemplateId(id);
    const item = STARTER_TEMPLATES.find((row) => row.id === id);
    if (!item) return;
    const mapped = ruleAstToDraft(item.rule_ast);
    setName(item.name);
    setJoinOperator(mapped.joinOperator);
    setConditions(mapped.conditions);
    setEditId(null);
    setRefineFromAi(false);
    setMappedConditions(mapped.mapped !== false);
    setPreviewCount(null);
  };

  const handleSave = async () => {
    if (!name.trim() || !ruleAst) return;
    setSaving(true);
    const payload = {
      name: name.trim(),
      join_operator: joinOperator,
      conditions,
      rule_ast: ruleAst,
      origin: "manual",
      is_test: Boolean(isTest),
    };
    if (refineFromAi) {
      payload.description = t(
        "abs.builder_refined_desc",
        "Refined from an AI-discovered segment"
      );
    } else if (!editId) {
      payload.description = t("abs.builder_saved_desc", "Created in Segment Builder");
    }
    const res = editId
      ? await updateSegment(editId, payload)
      : await createSegment(payload);
    setSaving(false);
    if (applyForbidden && applyForbidden(res)) return;
    if (!res || res.error || !res.item) {
      message.error(
        res?.error || t("abs.builder_save_error", "Could not save segment")
      );
      return;
    }
    message.success(
      editId
        ? t("abs.builder_updated", "Segment updated")
        : t("abs.builder_saved", "Segment saved")
    );
    if (onSaved) onSaved(res.item);
    resetForm();
  };

  return (
    <div>
      <div className={styles.listHeader}>
        <h3 className={styles.listTitle}>
          {editId
            ? t("abs.builder_edit_title", "Edit segment")
            : t("abs.builder_create", "Create a segment")}
        </h3>
        <button type="button" className={styles.viewAll} onClick={onViewLibrary}>
          {t("abs.builder_view_all", "View all segments →")}
        </button>
      </div>
      <div className={styles.panel}>
        {refineFromAi ? (
          <p className={styles.refineNote}>
            {mappedConditions
              ? t(
                  "abs.builder_refine_note",
                  "Prefilled based on this segment — review before saving. This is a starting point, not an exact copy of the AI logic."
                )
              : t(
                  "abs.builder_refine_name_only",
                  "Opened from an AI-discovered segment. Its rule could not be mapped to Builder fields — add conditions, then review before saving."
                )}
          </p>
        ) : null}
        <Input
          className={styles.name}
          placeholder={t("abs.builder_name", "Segment name")}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Select
          className={styles.template}
          allowClear
          placeholder={t("abs.builder_template", "Start from a template…")}
          value={templateId}
          onChange={(value) => {
            if (!value) {
              setTemplateId(undefined);
              return;
            }
            applyTemplate(value);
          }}
          options={STARTER_TEMPLATES.map((item) => ({
            value: item.id,
            label: item.name,
          }))}
        />
        <div className={styles.join}>
          {t("abs.builder_match", "Match")}
          <Select
            size="small"
            value={joinOperator}
            onChange={setJoinOperator}
            options={[
              { value: "AND", label: t("abs.builder_all", "all") },
              { value: "OR", label: t("abs.builder_any", "any") },
            ]}
          />
          {t("abs.builder_conditions", "of the following conditions:")}
        </div>
        <div className={styles.rows}>
          {conditions.map((condition) => (
            <ConditionRow
              key={condition.id}
              condition={condition}
              onChange={(next) =>
                setConditions((prev) =>
                  prev.map((row) => (row.id === condition.id ? next : row))
                )
              }
              onRemove={() =>
                setConditions((prev) =>
                  prev.length === 1
                    ? [newCondition()]
                    : prev.filter((row) => row.id !== condition.id)
                )
              }
              t={t}
            />
          ))}
        </div>
        <button
          type="button"
          className={styles.add}
          onClick={() => setConditions((prev) => [...prev, newCondition()])}
        >
          <Plus size={14} strokeWidth={2} />
          {t("abs.builder_add", "Add condition")}
        </button>
        <div className={styles.preview}>
          {previewLoading
            ? t("abs.builder_preview_loading", "Counting matching people…")
            : previewCount == null
              ? t(
                  "abs.builder_preview_placeholder",
                  "Matching audience preview will appear here"
                )
              : t("abs.builder_preview", "~{{count}} people match this rule", {
                  count: previewCount,
                })}
        </div>
        <div className={styles.actions}>
          <Button
            onClick={() => {
              resetForm();
              if (onViewLibrary && (editId || refineFromAi)) onViewLibrary();
            }}
          >
            {t("abs.builder_cancel", "Cancel")}
          </Button>
          <Button
            type="primary"
            loading={saving}
            disabled={!name.trim() || !ruleAst}
            onClick={handleSave}
          >
            {editId
              ? t("abs.builder_update", "Update segment")
              : t("abs.builder_save", "Save segment")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SegmentBuilder;
